import { complianceApi } from './complianceApiBase';
import { ComplianceItemView, ComplianceDocumentView } from './types/complianceTypes';
import { ComplianceUpsertRequest } from './requests/complianceItemUpsertRequest';
import { ComplianceCompleteRequest } from './requests/complianceItemCompleteRequest';
import { ComplianceItemsSeedRequest, ComplianceItemsSeedResponse } from './requests/complianceItemsSeedRequest';
import { ComplianceChecklistItemsUpdateRequest } from './requests/complianceChecklistItemsUpdateRequest';

export const complianceMutationsApi = complianceApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create or update a compliance requirement (upsert)
    upsertComplianceItem: builder.mutation<ComplianceItemView, ComplianceUpsertRequest>({
      query: (body) => ({
        url: 'Compliance',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'ComplianceList',
        { type: 'Compliance', id: arg.complianceItemView.id || 'NEW' },
      ],
    }),

    // Mark a compliance requirement complete
    completeComplianceItem: builder.mutation<ComplianceItemView, ComplianceCompleteRequest>({
      query: (body) => ({
        url: 'Compliance/Complete',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'ComplianceList',
        { type: 'Compliance', id: arg.id },
      ],
    }),

    // Seed every applicable standard requirement (data-model.md's 14-item catalog) for one
    // facility. Idempotent — omit facilityType for the auto-derived route, or supply it for the
    // explicit-confirmation route (contracts/lifesafety-eoc-api.md).
    seedComplianceItems: builder.mutation<ComplianceItemsSeedResponse, ComplianceItemsSeedRequest>({
      query: ({ centerId, facilityType }) => ({
        url: facilityType ? `Compliance/seed/${centerId}/${facilityType}` : `Compliance/seed/${centerId}`,
        method: 'POST',
      }),
      invalidatesTags: ['ComplianceList'],
    }),

    // Replace an EOC compliance item's checklist item list wholesale (US3 customization)
    updateChecklistItems: builder.mutation<ComplianceItemView, ComplianceChecklistItemsUpdateRequest>({
      query: (body) => ({
        url: 'Compliance/ChecklistItems',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'ComplianceList',
        { type: 'Compliance', id: arg.id },
      ],
    }),
  }),
});

// Document mutations (from #99, unmodified) — kept in their own `injectEndpoints` call so this
// file's per-function line count stays under CodeScene's Large Method threshold.
export const complianceDocumentMutationsApi = complianceApi.injectEndpoints({
  endpoints: (builder) => ({
    // Upload a document to a compliance requirement (calls #99's existing, unmodified endpoint)
    uploadComplianceDocument: builder.mutation<ComplianceDocumentView, { complianceItemId: string; file: File; documentType: string }>({
      query: ({ complianceItemId, file, documentType }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);
        return {
          url: `compliance/${complianceItemId}/documents`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { complianceItemId }) => [
        { type: 'ComplianceDocument', id: complianceItemId },
        'ComplianceDocument',
      ],
    }),

    // Download a document
    downloadComplianceDocument: builder.mutation<Blob, { complianceItemId: string; documentId: string }>({
      query: ({ complianceItemId, documentId }) => ({
        url: `compliance/${complianceItemId}/documents/${documentId}/download`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Delete a document
    deleteComplianceDocument: builder.mutation<void, { complianceItemId: string; documentId: string }>({
      query: ({ complianceItemId, documentId }) => ({
        url: `compliance/${complianceItemId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { complianceItemId }) => [
        { type: 'ComplianceDocument', id: complianceItemId },
        'ComplianceDocument',
      ],
    }),
  }),
});

export const {
  useUpsertComplianceItemMutation,
  useCompleteComplianceItemMutation,
  useSeedComplianceItemsMutation,
  useUpdateChecklistItemsMutation,
} = complianceMutationsApi;

export const {
  useUploadComplianceDocumentMutation,
  useDownloadComplianceDocumentMutation,
  useDeleteComplianceDocumentMutation,
} = complianceDocumentMutationsApi;
